<template>
  <div>
    <b-modal
      :id="id"
      :ref="id"
      v-model="internalVisible"
      hide-footer
      hide-header
      :hide-header-close="hideHeaderClose"
      centered
      :size="size"
      :no-close-on-backdrop="noCloseOnBackdrop"
      :no-close-on-esc="noCloseOnEsc"
      dialog-class="!max-w-[420px] !w-full mx-auto my-auto"
      content-class="rounded-[20px] border-0 overflow-hidden shadow-2xl"
      body-class="p-0"
      @show="onShow"
      @shown="onShown"
      @hide="onHide"
      @hidden="onHidden"
    >
      <div class="p-6 bg-white">
        <!-- Title -->
        <slot name="title">
          <h3 class="text-[20px] font-semibold text-center text-[#111827] mb-1.5 leading-snug">
            {{ title }}
          </h3>
        </slot>

        <!-- Subtitle with Channel & Masked Target -->
        <slot
          name="subtitle"
          :channel="channelLabel"
          :destination="displayDestination"
        >
          <div class="text-[13px] sm:text-[14px] text-center text-[#6B7280] leading-relaxed mb-2 max-w-[340px] mx-auto">
            <div>
              {{ subtitlePrefix }} {{ channelLabel }} ke
            </div>
            <div class="break-all">
              {{ displayDestination }}
            </div>
          </div>
        </slot>

        <!-- 6 OTP Input Boxes -->
        <slot
          name="inputs"
          :digits="digits"
          :has-error="hasError"
        >
          <div class="flex justify-center items-center gap-2.5 mb-1.5">
            <input
              v-for="(digit, index) in digits"
              :id="`${id}-input-${index}`"
              :key="index"
              ref="otpInputs"
              v-model="digits[index]"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="1"
              placeholder="_"
              :disabled="disabled || isProcessing || isSessionExpired"
              :class="[
                'otp-input-box',
                hasError
                  ? 'otp-box-error !border-[#F04438]'
                  : (activeFocusIndex === index ? 'otp-box-active !border-2 !border-[#6B7280]' : 'otp-box-normal !border-[#E5E7EB]'),
              ]"
              :style="hasError ? { borderColor: '#F04438', borderWidth: '1.5px', borderStyle: 'solid' } : {}"
              class="w-[48px] h-[52px] text-center text-[19px] font-medium rounded-[12px] bg-white outline-none select-none transition-colors duration-150 placeholder-[#4B5563] text-[#111827] caret-[#111827]"
              @input="handleInput($event, index)"
              @keydown="handleKeyDown($event, index)"
              @paste="handlePaste($event, index)"
              @focus="onInputFocus(index)"
              @blur="onInputBlur(index)"
            >
          </div>
        </slot>

        <!-- Validation Error Feedback -->
        <slot
          name="error"
          :error-message="errorMessage"
          :has-error="hasError"
          :remaining="remainingAttempts"
        >
          <div
            v-if="hasError && errorMessage"
            class="otp-error-text text-[13px] text-center text-[#F04438] font-normal my-2 transition-all duration-200"
          >
            {{ errorMessage }}
          </div>
        </slot>

        <!-- Countdown / Resend Section -->
        <slot
          name="resend"
          :countdown="isSessionExpired ? 0 : countdown"
          :resend="handleResend"
          :is-disabled="isResendDisabled"
        >
          <div class="text-center my-2.5 text-[14px]">
            <span
              v-if="countdown > 0 && !isSessionExpired"
              class="text-[#9CA3AF] select-none"
            >
              {{ resendText }} <span class="text-[#F95031] font-semibold">({{ countdown }}s)</span>
            </span>
            <button
              v-else
              type="button"
              :disabled="isResendDisabled"
              :class="[
                isResendDisabled
                  ? 'text-[#9CA3AF] cursor-not-allowed opacity-75'
                  : 'text-[#F95031] hover:text-[#E04526] cursor-pointer hover:underline',
                'font-medium focus:outline-none transition-colors duration-150'
              ]"
              @click="handleResend"
            >
              {{ resendText }}
            </button>
          </div>
        </slot>

        <!-- Actions -->
        <slot
          name="actions"
          :verify="handleSubmitButtonClick"
          :use-other-method="handleUseOtherMethod"
          :is-complete="isComplete"
          :countdown="isSessionExpired ? 0 : countdown"
          :loading="isProcessing"
          :is-session-expired="isSessionExpired"
          :reload-page="handleReloadPage"
        >
          <div class="flex flex-col gap-2.5 mt-3">
            <!-- Submit (Verifikasi / Kirim) Button -->
            <slot
              name="submit-button"
              :verify="handleSubmitButtonClick"
              :reload="handleReloadPage"
              :disabled="isSubmitDisabled"
              :loading="isProcessing"
              :submit-text="currentSubmitText"
              :is-session-expired="isSessionExpired"
            >
              <button
                type="button"
                :disabled="isSubmitDisabled"
                :class="[
                  isSubmitDisabled
                    ? 'bg-[#BCBCBC] text-white cursor-not-allowed'
                    : 'bg-[#F95031] text-white hover:bg-[#E04526] cursor-pointer shadow-sm active:transform active:scale-[0.99]',
                ]"
                class="w-full h-[46px] rounded-[12px] font-semibold text-[15px] focus:outline-none transition-all duration-150 flex items-center justify-center gap-2"
                @click="handleSubmitButtonClick"
              >
                <b-spinner
                  v-if="isProcessing"
                  small
                  variant="light"
                  class="mr-1"
                />
                <span>{{ currentSubmitText }}</span>
              </button>
            </slot>

            <!-- Gunakan Metode Lain Button -->
            <slot
              name="other-method-button"
              :use-other-method="handleUseOtherMethod"
              :disabled="isOtherMethodDisabled"
              :countdown="isSessionExpired ? 0 : countdown"
              :is-session-expired="isSessionExpired"
            >
              <button
                type="button"
                :disabled="isOtherMethodDisabled"
                :class="[
                  isOtherMethodDisabled
                    ? 'cursor-not-allowed border-[#E5E7EB] text-[#9CA3AF] bg-[#F9FAFB] opacity-75 pointer-events-none'
                    : 'border-[#D1D5DB] text-[#111827] bg-white hover:bg-gray-50 cursor-pointer active:transform active:scale-[0.99]',
                ]"
                class="w-full h-[46px] rounded-[12px] border font-medium text-[15px] focus:outline-none transition-all duration-150 flex items-center justify-center select-none"
                @click="handleUseOtherMethod"
              >
                <span>{{ otherMethodText }} <span
                  v-if="countdown > 0 && !isSessionExpired"
                  class="font-semibold text-[#9CA3AF]"
                >({{ countdown }}s)</span></span>
              </button>
            </slot>
          </div>
        </slot>
      </div>
    </b-modal>

    <!-- Modal Verifikasi Terblokir -->
    <modal-verifikasi-terblokir
      v-if="autoShowBlockedModal"
      :id="blockedModalId"
      ref="modalVerifikasiTerblokir"
      :title="blockedModalTitle"
      :description="blockedModalDescription"
      :cs-button-text="blockedModalCsText"
      :close-button-text="blockedModalCloseText"
      :cs-url="csUrl"
      @contact-cs="$emit('contact-cs')"
    />
  </div>
</template>

<script>
import ModalVerifikasiTerblokir from './ModalVerifikasiTerblokir.vue'

export default {
  name: 'ModalInputOTP',
  components: {
    ModalVerifikasiTerblokir,
  },
  props: {
    id: {
      type: String,
      default: 'modal-input-otp',
    },
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Masukkan Kode Verifikasi',
    },
    subtitlePrefix: {
      type: String,
      default: 'Masukkan kode OTP yang telah dikirim melalui',
    },
    channel: {
      type: String,
      default: 'email',
    },
    destination: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    phone: {
      type: String,
      default: '',
    },
    phoneNumber: {
      type: String,
      default: '',
    },
    autoMask: {
      type: Boolean,
      default: true,
    },
    submitText: {
      type: String,
      default: 'Verifikasi',
    },
    otherMethodText: {
      type: String,
      default: 'Gunakan Metode Lain',
    },
    resendText: {
      type: String,
      default: 'Kirim Ulang',
    },
    initialResendAttempt: {
      type: Number,
      default: 0,
    },
    rateLimits: {
      type: Array,
      default: () => [60, 120, 240, 480, 960],
    },
    maxAttempts: {
      type: Number,
      default: 3,
    },
    mockCode: {
      type: String,
      default: '123456',
    },
    verifyHandler: {
      type: Function,
      default: null,
    },
    modalRequestOtpId: {
      type: String,
      default: 'modal-request-otp',
    },
    autoReopenRequestModal: {
      type: Boolean,
      default: true,
    },
    blockedModalId: {
      type: String,
      default: 'modal-verifikasi-terblokir',
    },
    autoShowBlockedModal: {
      type: Boolean,
      default: true,
    },
    blockedModalTitle: {
      type: String,
      default: 'Verifikasi Terblokir',
    },
    blockedModalDescription: {
      type: String,
      default: 'Kamu telah salah memasukkan kode OTP sebanyak 3 kali. Demi keamanan akunmu, proses verifikasi diblokir sementara. Silakan coba lagi nanti atau hubungi CS kami.',
    },
    blockedModalCsText: {
      type: String,
      default: 'Hubungi CS',
    },
    blockedModalCloseText: {
      type: String,
      default: 'Tutup',
    },
    csUrl: {
      type: String,
      default: 'https://api.whatsapp.com/send?phone=6281328014084&text=Halo%20Admin%2C%20akun%20saya%20terblokir%20karena%20salah%20input%20OTP',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: true,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: true,
    },
    hideHeaderClose: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    usableToken: {
      type: String,
      default: '',
      required: false,
    },
    expiresIn: {
      type: [Number, String],
      default: null,
    },
    isLimitExceeded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const propExpiresIn = this.expiresIn != null ? this.expiresIn : this.$attrs.expires_in
    const initialDuration = propExpiresIn != null && !Number.isNaN(Number(propExpiresIn)) && Number(propExpiresIn) >= 0
      ? Number(propExpiresIn)
      : 0

    return {
      internalVisible: this.value,
      digits: ['', '', '', '', '', ''],
      activeFocusIndex: -1,
      countdown: initialDuration,
      timerInterval: null,
      resendAttemptCount: this.initialResendAttempt,
      failedAttempts: 0,
      hasError: false,
      errorMessage: '',
      internalLoading: false,
      isSessionExpired: false,
    }
  },
  computed: {
    effectiveExpiresIn() {
      const val = this.expiresIn != null ? this.expiresIn : this.$attrs.expires_in
      if (val != null && val !== '' && !Number.isNaN(Number(val)) && Number(val) > 0) {
        return Number(val)
      }
      return null
    },
    isProcessing() {
      return this.loading || this.internalLoading
    },
    otpValue() {
      return this.digits.join('')
    },
    isComplete() {
      return this.digits.every(d => d !== '') && this.otpValue.length === 6
    },
    isSubmitDisabled() {
      if (this.isSessionExpired) {
        return this.isProcessing
      }
      return !this.isComplete || this.isProcessing || this.disabled
    },
    isOtherMethodDisabled() {
      return this.countdown > 0 || this.isProcessing || this.disabled || this.isSessionExpired
    },
    isResendDisabled() {
      return this.isProcessing || this.resendAttemptCount >= 5 || this.isLimitExceeded || this.isSessionExpired
    },
    currentSubmitText() {
      if (this.isSessionExpired) {
        return 'Muat Ulang Halaman'
      }
      return this.submitText
    },
    remainingAttempts() {
      return Math.max(this.maxAttempts - this.failedAttempts, 0)
    },
    channelLabel() {
      const ch = (this.channel || '').toLowerCase()
      if (ch === 'email') return 'email'
      if (ch === 'whatsapp') return 'WhatsApp'
      if (ch === 'sms') return 'SMS'
      return this.channel || 'email'
    },
    effectivePhone() {
      return this.phoneNumber || this.phone
    },
    displayDestination() {
      if (this.destination) {
        if (this.destination.includes('*')) return this.destination
        if (this.destination.includes('@')) {
          return this.autoMask ? this.censorEmail(this.destination) : this.destination
        }
        return this.autoMask ? this.censorPhone(this.destination) : this.destination
      }

      const ch = (this.channel || '').toLowerCase()
      if (ch === 'whatsapp' || ch === 'sms') {
        if (!this.effectivePhone) return '-'
        return this.autoMask ? this.censorPhone(this.effectivePhone) : this.effectivePhone
      }

      if (!this.email) return '-'
      return this.autoMask ? this.censorEmail(this.email) : this.email
    },
  },
  watch: {
    value(newVal) {
      this.internalVisible = newVal
      if (newVal) {
        this.startCountdownTimer()
      } else {
        this.clearCountdownTimer()
      }
    },
    internalVisible(newVal) {
      this.$emit('input', newVal)
      if (newVal && !this.timerInterval) {
        this.startCountdownTimer()
      } else if (!newVal) {
        this.clearCountdownTimer()
      }
    },
    initialResendAttempt(newVal) {
      this.resendAttemptCount = newVal
    },
    expiresIn(newVal) {
      if (newVal != null && !Number.isNaN(Number(newVal)) && Number(newVal) >= 0) {
        this.startCountdownTimer(Number(newVal))
      }
    },
    '$attrs.expires_in': function handleAttrsExpiresIn(newVal) {
      if (newVal != null && !Number.isNaN(Number(newVal)) && Number(newVal) >= 0) {
        this.startCountdownTimer(Number(newVal))
      }
    },
    isSessionExpired(newVal) {
      if (newVal) {
        this.clearCountdownTimer()
        this.countdown = 0
      }
    },
  },
  mounted() {
    if (this.internalVisible) {
      this.startCountdownTimer()
    }
  },
  beforeDestroy() {
    this.clearCountdownTimer()
  },
  methods: {
    censorEmail(val) {
      if (!val) return ''
      if (val.includes('*')) return val
      const parts = val.split('@')
      if (parts.length !== 2) return val
      const [local, domain] = parts
      const maskedLocal = local.length > 3
        ? `${local.slice(0, 3)}${'*'.repeat(Math.max(local.length, 18))}`
        : `${local.slice(0, 1)}***`

      const domainParts = domain.split('.')
      if (domainParts.length >= 2) {
        const dName = domainParts[0]
        const ext = domainParts.slice(1).join('.')
        const maskedDomain = `${dName.charAt(0)}${'*'.repeat(Math.max(dName.length, 5))}.${ext}`
        return `${maskedLocal}@${maskedDomain}`
      }
      return `${maskedLocal}@${domain}`
    },
    censorPhone(val) {
      if (!val) return ''
      if (val.includes('*')) return val
      const cleanPhone = String(val).replace(/\s+/g, '')
      if (cleanPhone.length <= 4) return cleanPhone
      const visibleCount = 4
      const maskedCount = Math.max(cleanPhone.length - visibleCount, 8)
      return `${'*'.repeat(maskedCount)}${cleanPhone.slice(-visibleCount)}`
    },
    focusInput(index) {
      const inputs = this.$refs.otpInputs
      if (inputs && inputs[index]) {
        inputs[index].focus()
        if (inputs[index].select) {
          inputs[index].select()
        }
      }
    },
    onInputFocus(index) {
      this.activeFocusIndex = index
    },
    onInputBlur() {
      this.activeFocusIndex = -1
    },
    handleInput(event, index) {
      if (this.isSessionExpired) return
      const rawVal = event.target.value
      const cleaned = rawVal.replace(/\D/g, '')

      if (cleaned.length > 0) {
        const digit = cleaned.slice(-1)
        this.$set(this.digits, index, digit)
        this.resetError()
        this.emitChange()

        if (index < 5) {
          this.$nextTick(() => {
            this.focusInput(index + 1)
          })
        }
      } else {
        this.$set(this.digits, index, '')
        this.emitChange()
      }
    },
    handleKeyDown(event, index) {
      const { key } = event

      if (key === 'Backspace') {
        if (!this.digits[index] && index > 0) {
          event.preventDefault()
          this.$set(this.digits, index - 1, '')
          this.emitChange()
          this.focusInput(index - 1)
        }
        return
      }

      if (key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusInput(index - 1)
        return
      }

      if (key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        this.focusInput(index + 1)
        return
      }

      if (key === 'Enter') {
        if (this.isSessionExpired) {
          this.handleReloadPage()
          return
        }
        if (this.isComplete && !this.isSubmitDisabled) {
          this.handleVerify()
        }
        return
      }

      // Block non-numeric characters (except ctrl/cmd shortcuts)
      if (!/^\d$/.test(key) && !event.ctrlKey && !event.metaKey && key !== 'Tab') {
        event.preventDefault()
      }
    },
    handlePaste(event) {
      if (this.isSessionExpired) return
      event.preventDefault()
      const clipboardData = event.clipboardData || window.clipboardData
      if (!clipboardData) return

      const pastedText = clipboardData.getData('text') || ''
      const cleanedDigits = pastedText.replace(/\D/g, '').slice(0, 6)

      if (cleanedDigits.length > 0) {
        for (let i = 0; i < 6; i += 1) {
          this.$set(this.digits, i, cleanedDigits[i] || '')
        }
        this.resetError()
        this.emitChange()

        const targetFocusIndex = Math.min(cleanedDigits.length, 5)
        this.$nextTick(() => {
          this.focusInput(targetFocusIndex)
        })
      }
    },
    emitChange() {
      this.$emit('change', this.otpValue)
    },
    startCountdownTimer(forceDuration = null) {
      this.clearCountdownTimer()

      if (this.resendAttemptCount >= 5 || this.isLimitExceeded || this.isSessionExpired) {
        this.countdown = 0
        return
      }

      let duration = 0
      if (forceDuration !== null && !Number.isNaN(Number(forceDuration))) {
        duration = Number(forceDuration)
      } else if (this.effectiveExpiresIn !== null) {
        duration = this.effectiveExpiresIn
      }

      this.countdown = duration

      if (duration > 0) {
        const targetTime = Date.now() + duration * 1000
        this.timerInterval = setInterval(() => {
          const remaining = Math.max(0, Math.ceil((targetTime - Date.now()) / 1000))
          if (this.countdown !== remaining) {
            this.countdown = remaining
          }
          if (this.countdown <= 0) {
            this.countdown = 0
            this.clearCountdownTimer()
            this.$emit('timer-finished')
          }
        }, 1000)
      }
    },
    clearCountdownTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
    handleResend() {
      if (this.isResendDisabled || this.countdown > 0) return

      if (this.resendAttemptCount >= 5) {
        if (this.$toast_error) {
          this.$toast_error({
            message: 'Batas maksimal pengiriman OTP tercapai. Permintaan diblokir selama 24 jam.',
          })
        }
        this.$emit('limit-reached', {
          resendAttemptCount: this.resendAttemptCount,
        })
        return
      }

      this.resendAttemptCount += 1

      this.$emit('resend', {
        attempt: this.resendAttemptCount,
        channel: this.channel,
        destination: this.displayDestination,
      })
    },
    handleUseOtherMethod() {
      if (this.isOtherMethodDisabled) return

      this.$emit('use-other-method', {
        resendAttemptCount: this.resendAttemptCount,
      })
      this.$emit('back', {
        resendAttemptCount: this.resendAttemptCount,
      })

      this.hide()

      if (this.autoReopenRequestModal && this.modalRequestOtpId && this.$bvModal) {
        this.$nextTick(() => {
          this.$bvModal.show(this.modalRequestOtpId)
        })
      }
    },
    handleSubmitButtonClick() {
      if (this.isSessionExpired) {
        this.handleReloadPage()
        return
      }
      this.handleVerify()
    },
    handleReloadPage() {
      this.$emit('reload-page')
      window.location.reload()
    },
    async handleVerify() {
      if (this.isSessionExpired) {
        this.handleReloadPage()
        return
      }
      if (this.isSubmitDisabled) return

      this.$emit('verify', this.otpValue)
      this.internalLoading = true
      const body = {
        token: this.usableToken,
        otp_value: this.otpValue,
      }
      try {
        const res = await this.$http_new.post('/auth/api/v2/otp/verify', body)
        if (res && res.data && res.data.data && res.data.data.valid) {
          this.handleSuccess(res, this.otpValue)
        } else {
          const resData = res && res.data
          const isLocked = Number(resData?.data?.lock) === 1
            || resData?.data?.lock === true
            || Number(resData?.meta?.data?.lock) === 1
            || resData?.meta?.data?.lock === true
            || Number(resData?.lock) === 1
            || resData?.lock === true
          const metaMsg = (resData && resData.meta && resData.meta.message)
            || (resData && resData.message)
            || (resData && resData.data && resData.data.message)
          const attemptLeft = (resData && resData.data && resData.data.attempt_left !== undefined)
            ? resData.data.attempt_left
            : (resData && resData.meta && resData.meta.data && resData.meta.data.attempt_left)
          let message = metaMsg === 'otp not valid' && attemptLeft !== undefined && attemptLeft !== null
            ? `Kode OTP salah. Kesempatan tersisa ${attemptLeft} kali`
            : (metaMsg || `Kode OTP salah. Sisa percobaan: ${Math.max(this.remainingAttempts - 1, 0)} kali`)
          if (String(message || '').toLowerCase().includes('expired token') || String(metaMsg || '').toLowerCase().includes('expired token')) {
            message = 'Sesi telah berakhir, silakan muat ulang halaman.'
            this.isSessionExpired = true
            this.clearCountdownTimer()
            this.countdown = 0
          }
          this.triggerError(message, attemptLeft, isLocked, resData)
        }
      } catch (err) {
        const errData = err && err.response && err.response.data
        const isLocked = Number(errData?.data?.lock) === 1
          || errData?.data?.lock === true
          || Number(errData?.meta?.data?.lock) === 1
          || errData?.meta?.data?.lock === true
          || Number(errData?.lock) === 1
          || errData?.lock === true
        const metaMsg = (errData && errData.meta && errData.meta.message)
          || (errData && errData.message)
          || (errData && errData.data && errData.data.message)
          || (err && err.message)
        const attemptLeft = (errData && errData.data && errData.data.attempt_left !== undefined)
          ? errData.data.attempt_left
          : (errData && errData.meta && errData.meta.data && errData.meta.data.attempt_left)
        let message = metaMsg === 'otp not valid' && attemptLeft !== undefined && attemptLeft !== null
          ? `Kode OTP salah. Kesempatan tersisa ${attemptLeft} kali`
          : (metaMsg || 'Terjadi kesalahan. Silakan coba lagi.')
        if (String(message || '').toLowerCase().includes('expired token') || String(metaMsg || '').toLowerCase().includes('expired token')) {
          message = 'Sesi telah berakhir, silakan muat ulang halaman.'
          this.isSessionExpired = true
          this.clearCountdownTimer()
          this.countdown = 0
        }
        this.triggerError(message, attemptLeft, isLocked, errData)
      } finally {
        this.internalLoading = false
      }
    },
    handleSuccess(result, otp) {
      this.resetError()
      this.$emit('success', result, otp)
      this.hide()
    },
    triggerError(customMessage = '', attemptLeft = null, isLocked = false, rawData = null) {
      let finalMessage = customMessage
      if (typeof finalMessage === 'string' && finalMessage.toLowerCase().includes('expired token')) {
        this.isSessionExpired = true
        finalMessage = 'Sesi telah berakhir, silakan muat ulang halaman.'
      }

      if (this.isSessionExpired) {
        this.clearCountdownTimer()
        this.countdown = 0
        this.hasError = true
        this.errorMessage = finalMessage || 'Sesi telah berakhir, silakan muat ulang halaman.'
        this.digits = ['', '', '', '', '', '']
        this.emitChange()
        this.activeFocusIndex = -1
        this.$emit('error', {
          failedAttempts: this.failedAttempts,
          remaining: this.remainingAttempts,
          errorMessage: this.errorMessage,
          isSessionExpired: true,
        })
        return
      }

      if (attemptLeft !== null && attemptLeft !== undefined && !Number.isNaN(Number(attemptLeft))) {
        this.failedAttempts = Math.max(this.maxAttempts - Number(attemptLeft), this.failedAttempts + 1)
      } else {
        this.failedAttempts += 1
      }

      if (isLocked || this.failedAttempts >= this.maxAttempts || attemptLeft === 0) {
        this.hide()
        this.$emit('blocked', {
          failedAttempts: this.failedAttempts,
          maxAttempts: this.maxAttempts,
          attemptLeft,
          isLocked: !!isLocked,
          lock: isLocked ? 1 : 0,
          lockSource: (rawData && (rawData.data?.lock_source || rawData.lock_source)) || 'otp',
          data: rawData,
        })
        if (this.autoShowBlockedModal && this.$bvModal) {
          this.$nextTick(() => {
            this.$bvModal.show(this.blockedModalId)
          })
        }
        return
      }

      this.hasError = true
      this.errorMessage = finalMessage || `Kode OTP salah. Sisa percobaan: ${this.remainingAttempts} kali`

      // Bersihkan kotak input OTP dan kembalikan fokus ke kotak pertama (AC08 / AC09)
      this.digits = ['', '', '', '', '', '']
      this.emitChange()

      this.activeFocusIndex = 0
      this.$nextTick(() => {
        this.focusInput(0)
      })

      this.$emit('error', {
        failedAttempts: this.failedAttempts,
        remaining: this.remainingAttempts,
        errorMessage: this.errorMessage,
      })
    },
    resetError() {
      if (this.isSessionExpired) return
      this.hasError = false
      this.errorMessage = ''
    },
    resetAttempts() {
      this.failedAttempts = 0
      this.resetError()
    },
    resetRateLimit(newAttempt = 0) {
      this.resendAttemptCount = newAttempt
    },
    reset() {
      this.digits = ['', '', '', '', '', '']
      this.isSessionExpired = false
      this.hasError = false
      this.errorMessage = ''
      this.emitChange()
    },
    show(duration = null) {
      this.internalVisible = true
      if (duration !== null && !Number.isNaN(Number(duration))) {
        this.startCountdownTimer(Number(duration))
      } else {
        this.startCountdownTimer()
      }
      if (this.$bvModal) {
        this.$bvModal.show(this.id)
      }
    },
    setCountdown(seconds) {
      if (this.isSessionExpired) {
        this.countdown = 0
        return
      }
      if (seconds != null && !Number.isNaN(Number(seconds))) {
        this.startCountdownTimer(Number(seconds))
      }
    },
    hide() {
      this.internalVisible = false
      if (this.$bvModal) {
        this.$bvModal.hide(this.id)
      }
    },
    onShow() {
      this.$emit('show')
      this.startCountdownTimer()
    },
    onShown() {
      this.$emit('shown')
      this.reset()

      if (!this.timerInterval) {
        this.startCountdownTimer()
      }

      this.$nextTick(() => {
        this.focusInput(0)
      })
    },
    onHide(bvEvent) {
      if (bvEvent && bvEvent.trigger === 'backdrop' && this.noCloseOnBackdrop) {
        bvEvent.preventDefault()
        return
      }
      if (bvEvent && bvEvent.trigger === 'esc' && this.noCloseOnEsc) {
        bvEvent.preventDefault()
        return
      }
      this.$emit('hide', bvEvent)
    },
    onHidden() {
      this.$emit('hidden')
      this.reset()
      this.clearCountdownTimer()
    },
  },
}
</script>

<style scoped>
.otp-input-box {
  width: 48px;
  height: 52px;
  text-align: center;
  font-size: 19px;
  font-weight: 500;
  border-radius: 12px;
  background-color: #ffffff;
  outline: none !important;
  user-select: none;
  transition: border-color 0.15s ease-in-out;
  color: #111827;
  caret-color: #111827;
  border: 1px solid #E5E7EB !important;
}

.otp-input-box::placeholder {
  color: #4B5563;
}

.otp-input-box:focus,
.otp-input-box.otp-box-active {
  border: 2px solid #6B7280 !important;
}

/* ERROR STATE: Semua kotak input border berubah merah */
.otp-input-box.otp-box-error,
.otp-input-box.otp-box-error:focus,
.otp-input-box.otp-box-error.otp-box-active {
  border: 1.5px solid #F04438 !important;
  border-color: #F04438 !important;
  caret-color: #F04438 !important;
}

.otp-error-text {
  color: #F04438 !important;
}
</style>

<style>
/* Global override to ensure Bootstrap or outside CSS cannot override the error border */
input.otp-input-box.otp-box-error,
input.otp-input-box.otp-box-error:focus {
  border: 1.5px solid #F04438 !important;
  border-color: #F04438 !important;
  caret-color: #F04438 !important;
}
</style>
